import { Box, Stack, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import ActionButtonSmall from "../../components/layout/buttons/action-button-small";
import API from "../../api/api";
import ApiUri from "../../api/api-uri";
import { GlobalLoaderContext } from "../../context/global-loader";
import BaseComponentLogin from "./baseComponentLogin";
import { isValidEmail } from ".";
import ReCAPTCHA from "react-google-recaptcha";
import { TranslationsContext } from "../../context/translations";
import { AuthContext } from "../../context/auth";
import { useParams } from "react-router";
import { setCookie } from "../../App";

const ResendEmail = () => {
  const { profile, app } = useParams();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const { startGlobalLoader, stopGlobalLoader } =
    useContext(GlobalLoaderContext);
  const { translations } = useContext(TranslationsContext);
  const { profileNameLogin, profileImageURLLogin } = useContext(AuthContext);

  document.title = profileNameLogin
    ? `${profileNameLogin} - Resend Email`
    : "Resend Email";

  useEffect(() => {
    setCookie(process.env.REACT_APP_MMAZ_APPLICATION_COOKIE, app, 365);
    setCookie(process.env.REACT_APP_MMAZ_PROFILE_COOKIE, profile, 365);
    setCookie(process.env.REACT_APP_MCZ_PROFILE_COOKIE, profile, 365);
  }, []);

  const [recaptchaApproved, setRecaptchaApproved] = useState(false);
  function onChangeRecaptcha(value) {
    if (value) {
      setRecaptchaApproved(true);
    } else {
      setRecaptchaApproved(false);
    }
  }

  const buttonSX = {
    backgroundColor: "#3399FE",
    borderRadius: "8px",
    fontWeight: 600,
    letterSpacing: "0.4px",
    padding: "10px",
    marginTop: 2,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#1a77d4",
      boxShadow: "0 6px 18px rgba(51,153,254,0.4)",
      transform: "translateY(-1px)",
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(51,153,254,0.28)",
      color: "rgba(255,255,255,0.55)",
    },
  };

  const handleClick = async (event) => {
    setFormError("");
    setFormSuccess("");
    startGlobalLoader();
    const payload = {
      email: email,
      application: app,
      profile: profile,
    };
    try {
      const response = await API.post(
        ApiUri.AUTH + ApiUri.ACTIVATION + ApiUri.RESEND,
        payload,
      );
      setFormSuccess(
        typeof response?.data?.data === "string"
          ? response.data.data
          : "Activation email sent. Please check your inbox."
      );
    } catch (error: any) {
      setFormError(
        error?.response?.data?.message ?? "An error occurred. Please try again."
      );
    } finally {
      stopGlobalLoader();
    }
  };

  const handleBlur = () => {
    setEmailError(!isValidEmail(email));
  };

  const emailIsValid = isValidEmail(email);

  const handleChangeEmail = (event) => {
    const input = event.target.value.trim();
    setEmail(input);
    if (formError) setFormError("");
    if (emailError) {
      setEmailError(!isValidEmail(input));
    }
  };

  const headerContent = (
    <Stack alignItems="center" spacing={1.5}>
      {profileImageURLLogin && (
        <img
          src={profileImageURLLogin}
          alt={profileNameLogin}
          style={{
            width: "76px",
            height: "76px",
            borderRadius: "50%",
            border: "2px solid rgba(51,153,254,0.55)",
            boxShadow:
              "0 0 0 4px rgba(51,153,254,0.1), 0 6px 20px rgba(0,0,0,0.4)",
          }}
        />
      )}
      <Box>
        <Typography
          align="center"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            fontSize: "16px",
            letterSpacing: "-0.2px",
          }}
        >
          {profileNameLogin || "Activation Email"}
        </Typography>
        <Typography
          align="center"
          sx={{
            color: "rgba(255,255,255,0.45)",
            fontSize: "10px",
            letterSpacing: "2.5px",
            textTransform: "uppercase",
            mt: 0.5,
          }}
        >
          {translations["heading.activation_email"] || "Activation Email"}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <BaseComponentLogin backButton headerSection={headerContent}>
      <TextField
        fullWidth
        size="small"
        label={translations["label.your_email"] || "Your Email"}
        value={email}
        onChange={handleChangeEmail}
        onBlur={handleBlur}
        error={emailError}
        inputProps={{ autoCapitalize: "none" }}
        sx={{ paddingBottom: 3 }}
      />

      <ReCAPTCHA
        sitekey={process.env.REACT_APP_SITEKEY_RECAPTCHA}
        onChange={onChangeRecaptcha}
      />

      <ActionButtonSmall
        sx={buttonSX}
        onClick={handleClick}
        disabled={email.length === 0 || !emailIsValid || !recaptchaApproved}
        fullWidth
      >
        {translations["button.resend_email"] || "Resend Email"}
      </ActionButtonSmall>

      {formError && (
        <Box
          sx={{
            mt: 2,
            width: "100%",
            px: 1.5,
            py: 1,
            borderRadius: "8px",
            backgroundColor: "rgba(211,47,47,0.08)",
            border: "1px solid rgba(211,47,47,0.25)",
          }}
        >
          <Typography fontSize={13} sx={{ color: "#c62828", textAlign: "center" }}>
            {formError}
          </Typography>
        </Box>
      )}

      {formSuccess && (
        <Box
          sx={{
            mt: 2,
            width: "100%",
            px: 1.5,
            py: 1,
            borderRadius: "8px",
            backgroundColor: "rgba(46,125,50,0.08)",
            border: "1px solid rgba(46,125,50,0.25)",
          }}
        >
          <Typography fontSize={13} sx={{ color: "#2e7d32", textAlign: "center" }}>
            {formSuccess}
          </Typography>
        </Box>
      )}
    </BaseComponentLogin>
  );
};

export default ResendEmail;
